import BackgroundImage, {IBackgroundImageProps} from 'gatsby-background-image'
import {GatsbyImageSharpFluid_WithWebpFragment} from 'gatsby-graphql-types-generated'
import {graphql} from 'gatsby'
import styled from '@emotion/styled'

import {containerShadow, containerHoverShadow} from 'src/styles/shadow'
import {animation, gradients, borders, theme} from 'src/styles'

export interface CardProps {
  hoverable?: boolean
  border?: keyof typeof gradients
  backgroundImage?: string | GatsbyImageSharpFluid_WithWebpFragment
  children?: React.ReactNode
}

export const query = graphql`
  fragment CardBackgroundImage on File {
    childImageSharp {
      fluid(maxWidth: 900, quality: 100) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }
`

const ShadowWrapper = styled.div<{hoverable?: boolean}>(
  {
    position: 'relative',
    transformStyle: 'preserve-3d',
    height: '100%',
    '&:after': {
      ...containerShadow,
      transition: animation.hoverEnter('box-shadow'),

      content: `""`,
      transform: 'translateZ(-1px)',
      borderRadius: 19,

      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  ({hoverable}) => {
    if (!hoverable) {
      return
    }

    return {
      '&:hover': {
        '&:after': {
          ...containerHoverShadow,
          transition: animation.hoverLeave('box-shadow'),
        },
      },
    }
  }
)

const Container = styled.div<CardProps>(
  {
    position: 'relative',
    borderRadius: 19,
    padding: theme.space[4],
    backgroundColor: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    height: '100%',
    '&:after': {
      borderRadius: 19,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    },
    '&:before': {
      borderRadius: 19,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    },
  },
  ({border}) => {
    if (!border) {
      return
    }

    return borders.gradientBorder(gradients[border])
  },
  ({backgroundImage}) => {
    if (!backgroundImage) {
      return
    }

    return {
      backgroundImage: `url(${backgroundImage})`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    }
  }
)

const StyledBackgroundImage = Container.withComponent(BackgroundImage)

const isFluid = (x: any): x is IBackgroundImageProps['fluid'] =>
  Boolean(x) && typeof x !== 'string'

const Card = ({children, backgroundImage, border, hoverable}: CardProps) => {
  if (isFluid(backgroundImage)) {
    return (
      <ShadowWrapper hoverable={hoverable}>
        <StyledBackgroundImage
          backgroundColor={theme.colors.white}
          fluid={backgroundImage}
          border={border}
        >
          {children}
        </StyledBackgroundImage>
      </ShadowWrapper>
    )
  }

  return (
    <ShadowWrapper hoverable={hoverable}>
      <Container border={border} backgroundImage={backgroundImage}>
        {children}
      </Container>
    </ShadowWrapper>
  )
}

export default Card
